import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import Amplitude from "@/shared/services/amplitude.service";
import { getCurrentURL, getScreenWidth } from "@/shared/utils/commons";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import NProgress from "nprogress";
import { MouseEvent } from "react";
import { OtherOfferingsMetaDataType } from "../organisms/OtherOfferingsSection";

export type PricingOfferingsSectionPropType = {
  title: string;
  description: string;
  productOfferings: OtherOfferingsMetaDataType[];
  featureOfferings: OtherOfferingsMetaDataType[];
};

const PricingOfferingsSection = ({
  title,
  description,
  productOfferings,
  featureOfferings,
}: PricingOfferingsSectionPropType) => {
  const router = useRouter();

  const handleDelayedHref = (e: MouseEvent, ctaHref: string, name: string) => {
    e.preventDefault();
    const { current_url, screen_width, utm_params, card_title } =
      LANDING_PAGE_EVENTS.OFFERING_CARD_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: "lp_offerings_card_tapped",
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [card_title]: name,
      } as { [key: string]: string },
    });
    NProgress.start();
    setTimeout(() => {
      router.push(ctaHref);
    }, 500);
  };

  return (
    <div className="container flex flex-col gap-6 sm:gap-8">
      <div className="flex flex-col gap-1">
        <h3 className="text-left text-title3 font-semibold text-grey-4 sm:text-title1">
          {title}
        </h3>
        <p className="text-body-14 text-grey-3">{description}</p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3 text-center">
          {productOfferings.map(({ name, description, image, path }, index) => (
            <>
              {path && (
                <Link
                  href={path}
                  className={classNames(
                    "flex flex-shrink flex-grow  basis-48 flex-col items-center gap-3 rounded-2xl bg-purple-0 px-6 pb-4 pt-6",
                    {
                      // If there are more than 4 productOfferings, then make the them render only 3 per row using flex-wrap
                      "min-w-[250px]": productOfferings.length > 4,
                    }
                  )}
                  key={index}
                  onClick={(e) => handleDelayedHref(e, path, name)}
                >
                  <div className="h-16 w-16 flex-shrink-0 basis-16">
                    <Image src={image} alt={name} />
                  </div>
                  <div className="flex flex-grow flex-col gap-px">
                    <h4 className="text-title6 font-semibold text-grey-4">
                      {name}
                    </h4>
                    <p className="text-body text-grey-3">{description}</p>
                  </div>
                </Link>
              )}
            </>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 text-left sm:grid-cols-2 md:grid-cols-3">
          {featureOfferings.map(({ name, description, image, path }, index) => (
            <>
              {path && (
                <Link
                  href={path}
                  className="flex items-center gap-3 rounded-2xl bg-purple-0 py-3 pl-6 pr-4"
                  key={index}
                  onClick={(e) => handleDelayedHref(e, path, name)}
                >
                  <h4 className="flex-grow text-title6 font-semibold text-grey-4">
                    {name}
                  </h4>
                  <div className="h-10 w-10 flex-shrink-0 basis-10">
                    <Image src={image} alt={name} />
                  </div>
                </Link>
              )}
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingOfferingsSection;
